import React, { useState } from 'react';
import { PaintBucketIcon, SearchIcon, StarIcon, XIcon, CheckIcon, ChevronRightIcon } from 'lucide-react';
interface ColorMaterialMenuProps {
  position: {
    x: number;
    y: number;
  };
  onClose: () => void;
  onSelect: (material: any) => void;
}
export function ColorMaterialMenu({
  position,
  onClose,
  onSelect
}: ColorMaterialMenuProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  // Adjust position to ensure it's visible on screen
  const adjustPosition = () => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const menuWidth = 320;
    const menuHeight = 480;
    let x = position.x;
    let y = position.y;
    if (x + menuWidth > windowWidth) {
      x = windowWidth - menuWidth - 10;
    }
    if (y + menuHeight > windowHeight) {
      y = windowHeight - menuHeight - 10;
    }
    return {
      x,
      y
    };
  };
  const adjustedPosition = adjustPosition();
  const categories = [{
    id: 'all',
    name: 'All Materials'
  }, {
    id: 'wood',
    name: 'Wood'
  }, {
    id: 'lacquer',
    name: 'Lacquer'
  }, {
    id: 'glass',
    name: 'Glass'
  }, {
    id: 'metal',
    name: 'Metal'
  }, {
    id: 'stone',
    name: 'Stone'
  }, {
    id: 'favorites',
    name: 'Favorites'
  }];
  const materials = [
  // Wood materials
  {
    id: 'oak',
    name: 'Oak',
    category: 'wood',
    color: 'bg-amber-700',
    favorite: true
  }, {
    id: 'walnut',
    name: 'Walnut',
    category: 'wood',
    color: 'bg-amber-900'
  }, {
    id: 'maple',
    name: 'Maple',
    category: 'wood',
    color: 'bg-amber-200'
  }, {
    id: 'cherry',
    name: 'Cherry',
    category: 'wood',
    color: 'bg-red-800'
  },
  // Lacquer materials
  {
    id: 'white-gloss',
    name: 'White Gloss',
    category: 'lacquer',
    color: 'bg-white border border-gray-200 dark:border-gray-600'
  }, {
    id: 'black-matte',
    name: 'Black Matte',
    category: 'lacquer',
    color: 'bg-gray-900'
  }, {
    id: 'navy-gloss',
    name: 'Navy Gloss',
    category: 'lacquer',
    color: 'bg-blue-900'
  }, {
    id: 'red-gloss',
    name: 'Red Gloss',
    category: 'lacquer',
    color: 'bg-red-600'
  },
  // Glass materials
  {
    id: 'clear-glass',
    name: 'Clear Glass',
    category: 'glass',
    color: 'bg-sky-100 border border-gray-200 dark:border-gray-600'
  }, {
    id: 'frosted-glass',
    name: 'Frosted Glass',
    category: 'glass',
    color: 'bg-gray-100 border border-gray-200 dark:border-gray-600'
  }, {
    id: 'tinted-glass',
    name: 'Tinted Glass',
    category: 'glass',
    color: 'bg-blue-200 border border-gray-200 dark:border-gray-600'
  },
  // Metal materials
  {
    id: 'stainless-steel',
    name: 'Stainless Steel',
    category: 'metal',
    color: 'bg-gray-300',
    favorite: true
  }, {
    id: 'brushed-brass',
    name: 'Brushed Brass',
    category: 'metal',
    color: 'bg-yellow-700'
  }, {
    id: 'copper',
    name: 'Copper',
    category: 'metal',
    color: 'bg-amber-600'
  },
  // Stone materials
  {
    id: 'marble',
    name: 'Marble',
    category: 'stone',
    color: 'bg-gray-200'
  }, {
    id: 'granite',
    name: 'Granite',
    category: 'stone',
    color: 'bg-gray-800'
  }, {
    id: 'quartz',
    name: 'Quartz',
    category: 'stone',
    color: 'bg-gray-100 border border-gray-200 dark:border-gray-600',
    favorite: true
  }];
  const filteredMaterials = materials.filter(material => {
    // Filter by search query
    const matchesSearch = material.name.toLowerCase().includes(searchQuery.toLowerCase());
    // Filter by category
    const matchesCategory = activeCategory === 'all' || material.category === activeCategory || activeCategory === 'favorites' && material.favorite;
    return matchesSearch && matchesCategory;
  });
  const handleSelectMaterial = material => {
    setSelectedMaterial(material);
    onSelect(material);
    onClose();
  };
  return <div className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden w-80 dark:bg-darkbg-secondary dark:border-darkbg-border" style={{
    left: adjustedPosition.x,
    top: adjustedPosition.y
  }} data-id="element-57">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-darkbg-border" data-id="element-58">
        <div className="flex items-center" data-id="element-59">
          <PaintBucketIcon className="h-5 w-5 text-blue-600 mr-2 dark:text-accent-blue" data-id="element-60" />
          <h3 className="font-medium text-gray-800 dark:text-gray-200" data-id="element-61">
            Materials & Colors
          </h3>
        </div>
        <button className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300" onClick={onClose} data-id="element-62">
          <XIcon className="h-5 w-5" data-id="element-63" />
        </button>
      </div>
      {/* Search */}
      <div className="p-4 border-b border-gray-200 dark:border-darkbg-border" data-id="element-64">
        <div className="relative" data-id="element-65">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-66">
            <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-67" />
          </div>
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full pl-10 p-2.5 dark:bg-darkbg-primary dark:border-darkbg-border dark:text-white" placeholder="Search materials..." data-id="element-68" />
        </div>
      </div>
      {/* Categories */}
      <div className="p-2 border-b border-gray-200 flex overflow-x-auto dark:border-darkbg-border" data-id="element-69">
        {categories.map(category => <button key={category.id} className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap mr-1 ${activeCategory === category.id ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-accent-blue' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-darkbg-primary'}`} onClick={() => setActiveCategory(category.id)} data-id="element-70">
            {category.name}
          </button>)}
      </div>
      {/* Materials Grid */}
      <div className="p-4 h-64 overflow-y-auto" data-id="element-71">
        <div className="grid grid-cols-3 gap-3" data-id="element-72">
          {filteredMaterials.map(material => <button key={material.id} className="flex flex-col items-center" onClick={() => handleSelectMaterial(material)} data-id="element-73">
              <div className={`w-full h-16 ${material.color} rounded-md mb-1 relative`} data-id="element-74">
                {material.favorite && <div className="absolute top-1 right-1" data-id="element-75">
                    <StarIcon className="h-4 w-4 text-yellow-400 fill-current" data-id="element-76" />
                  </div>}
              </div>
              <span className="text-xs text-gray-700 text-center dark:text-gray-300" data-id="element-77">
                {material.name}
              </span>
            </button>)}
        </div>
        {filteredMaterials.length === 0 && <div className="text-center py-8 text-gray-500 dark:text-gray-400" data-id="element-78">
            No materials found matching your search.
          </div>}
      </div>
      {/* Footer */}
      <div className="p-3 border-t border-gray-200 flex justify-between dark:border-darkbg-border" data-id="element-79">
        <button className="text-sm text-blue-600 hover:text-blue-800 flex items-center dark:text-accent-blue dark:hover:text-blue-400" onClick={() => onClose()} data-id="element-80">
          <span data-id="element-81">View All Materials</span>
          <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-82" />
        </button>
        <button className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center dark:bg-accent-blue dark:hover:bg-blue-500" onClick={() => onClose()} data-id="element-83">
          <CheckIcon className="h-4 w-4 mr-1" data-id="element-84" />
          <span data-id="element-85">Apply</span>
        </button>
      </div>
    </div>;
}